import { useState, useMemo } from "react";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from "recharts";
import { toN, mkMap } from "../utils";
import { calcMonthlySchedule } from "./LoanLedger";
import { Card, ST, NI } from "../ui/SharedUI";

const REV_SITE_OPTIONS = [
  { value: "same",  label: "即時（同月）" },
  { value: "next",  label: "翌月入金" },
  { value: "next2", label: "翌々月入金" },
];

const EXP_SITE_OPTIONS = [
  { value: "same",  label: "即時（当月）" },
  { value: "next",  label: "翌月出金" },
  { value: "next2", label: "翌々月出金" },
];

const ACCENT = "#1A7A7A";

function shiftBySite(monthlyMap, months, site) {
  const vals = months.map((m) => toN(monthlyMap[m]));
  if (site === "same") return { shifted: vals, carryover: 0 };
  if (site === "next") return { shifted: [0, ...vals.slice(0, -1)], carryover: vals[vals.length - 1] || 0 };
  if (site === "next2") return { shifted: [0, 0, ...vals.slice(0, -2)], carryover: (vals[vals.length - 1] || 0) + (vals[vals.length - 2] || 0) };
  return { shifted: vals, carryover: 0 };
}

function SignedInput({ value, onChange, style, w="72px" }) {
  const isEmpty = value === "" || value == null;
  return (
    <input
      type="text"
      value={isEmpty ? "" : String(value)}
      placeholder="—"
      onChange={e => {
        let next = e.target.value.replace(/[^0-9-]/g, "");
        if (next.includes("-")) {
          next = (next.startsWith("-") ? "-" : "") + next.replace(/-/g, "");
        }
        onChange(next);
      }}
      style={{
        width:w, padding:"4px 5px", border:"1px solid #D0D8D0", borderRadius:"4px",
        textAlign:"right", fontSize:"12px", fontFamily:"monospace",
        background: isEmpty ? "#FAFCFA" : "#fff",
        color: (value===0||value==="0") ? "#bbb" : "inherit",
        ...style,
      }}
    />
  );
}

export default function CashflowSimulator({ revenue, expenses, expenseItems, expOverride, staff, months, cfSettings, setCfSettings, loans }) {
  const [showHelp, setShowHelp] = useState(false);

  const autoVals = useMemo(() => ({
    salary:    Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.salary[m]),0)])),
    bonus:     Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.bonus[m]),0)])),
    transport: Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.transport[m]),0)])),
    insurance: Object.fromEntries(months.map(m => {
      const salaryTotal = staff.reduce((s,p)=>s+toN(p.salary[m]),0);
      const bonusTotal = staff.reduce((s,p)=>s+toN(p.bonus[m]),0);
      const officerTotal = toN(expenses["officer"]?.[m]);
      return [m, Math.round((salaryTotal + bonusTotal + officerTotal) * 0.15)];
    })),
  }), [staff, months, expenses]);

  const loanSchedule = useMemo(() => {
    return months.map((m) => {
      return (loans || []).reduce((sum, loan) => {
        const s = calcMonthlySchedule(loan, months);
        return sum + (s[m]?.total ?? 0);
      }, 0);
    });
  }, [loans, months]);

  const getRevSite = (revId) => cfSettings.revSites?.[revId] || "next";
  const setRevSite = (revId, site) => {
    setCfSettings(prev => ({...prev, revSites: {...(prev.revSites||{}), [revId]: site}}));
  };

  const getExpSite = (itemId) => cfSettings.expSites?.[itemId] ?? cfSettings.expSite ?? "next";
  const setExpItemSite = (itemId, site) => {
    setCfSettings(prev => ({...prev, expSites: {...(prev.expSites||{}), [itemId]: site}}));
  };

  const taxMonths = cfSettings.taxMonths || {};
  const updateTax = (taxKey, m, val) => {
    setCfSettings(prev => ({
      ...prev,
      taxMonths: {
        ...(prev.taxMonths||{}),
        [taxKey]: {...((prev.taxMonths||{})[taxKey]||mkMap(months,0)), [m]: val}
      }
    }));
  };

  const updateInsurance = (m, val) => {
    setCfSettings(prev => ({
      ...prev,
      insuranceMonths: {
        ...(prev.insuranceMonths || {}),
        [m]: val,
      }
    }));
  };

  const resetInsuranceMonth = (m) => {
    setCfSettings(prev => {
      const next = { ...(prev.insuranceMonths || {}) };
      delete next[m];
      return { ...prev, insuranceMonths: next };
    });
  };

  const adjustments = cfSettings.adjustments || [];

  const addAdjustment = () => {
    const id = 'adj_' + Math.random().toString(36).slice(2, 8);
    const monthly = Object.fromEntries(months.map(m => [m, '']));
    setCfSettings(prev => ({
      ...prev,
      adjustments: [...(prev.adjustments || []), { id, label: '調整項目', monthly, memo: '' }]
    }));
  };

  const removeAdjustment = (id) => {
    setCfSettings(prev => ({
      ...prev,
      adjustments: (prev.adjustments || []).filter(a => a.id !== id)
    }));
  };

  const updateAdjustmentLabel = (id, label) => {
    setCfSettings(prev => ({
      ...prev,
      adjustments: (prev.adjustments || []).map(a => a.id === id ? { ...a, label } : a)
    }));
  };

  const updateAdjustmentVal = (id, m, val) => {
    setCfSettings(prev => ({
      ...prev,
      adjustments: (prev.adjustments || []).map(a =>
        a.id === id ? { ...a, monthly: { ...a.monthly, [m]: val } } : a
      )
    }));
  };

  const updateAdjustmentMemo = (id, val) => {
    setCfSettings(prev => ({
      ...prev,
      adjustments: (prev.adjustments || []).map(a => a.id === id ? { ...a, memo: val } : a)
    }));
  };

  const cfData = useMemo(() => {
    const _openingBalance = toN(cfSettings.openingBalance);
    const _taxMonths = cfSettings.taxMonths || {};

    const _getPlannedExp = (id, m) => {
      if (id === "insurance") {
        const overrideVal = cfSettings.insuranceMonths?.[m];
        if (overrideVal !== undefined && overrideVal !== "") return toN(overrideVal);
        return autoVals.insurance?.[m] ?? 0;
      }
      const item = expenseItems.find(e => e.id === id);
      if (item?.auto && !expOverride[id]) return toN(autoVals[item.autoKey]?.[m]);
      return toN(expenses[id]?.[m]);
    };

    const inflows = months.map(() => 0);
    let endingReceivable = 0;
    revenue.forEach(r => {
      const site = cfSettings.revSites?.[r.id] || "next";
      const { shifted, carryover } = shiftBySite(r.monthly, months, site);
      shifted.forEach((v, i) => { inflows[i] += v; });
      if (carryover) endingReceivable += carryover;
    });

    const expOutflows = months.map(() => 0);
    let endingPayable = 0;
    expenseItems.forEach(item => {
      const site = cfSettings.expSites?.[item.id] ?? cfSettings.expSite ?? "next";
      const itemByMonth = Object.fromEntries(months.map(m => [m, _getPlannedExp(item.id, m)]));
      const { shifted, carryover } = shiftBySite(itemByMonth, months, site);
      shifted.forEach((v, i) => { expOutflows[i] += v; });
      if (carryover) endingPayable += carryover;
    });

    const taxOut = months.map(m => {
      let t = 0;
      Object.values(_taxMonths).forEach(taxMap => { t += toN(taxMap?.[m]); });
      return t;
    });

    const outflows = months.map((m, i) => expOutflows[i] + taxOut[i]);

    months.forEach((m, i) => {
      outflows[i] += loanSchedule[i];
    });

    const carryInflow1  = toN(cfSettings.carryInflow1);
    const carryInflow2  = toN(cfSettings.carryInflow2);
    const carryOutflow1 = toN(cfSettings.carryOutflow1);
    const carryOutflow2 = toN(cfSettings.carryOutflow2);
    if (carryInflow1) inflows[0] += carryInflow1;
    if (carryInflow2 && months.length > 1) inflows[1] += carryInflow2;
    if (carryOutflow1) outflows[0] += carryOutflow1;
    if (carryOutflow2 && months.length > 1) outflows[1] += carryOutflow2;

    const adjVals = months.map(m => {
      return (cfSettings.adjustments || []).reduce((s, a) => s + toN(a.monthly?.[m]), 0);
    });

    let balance = _openingBalance;
    return months.map((m, i) => {
      const net = inflows[i] - outflows[i] - adjVals[i];
      balance += net;
      return {
        name: m,
        入金: inflows[i],
        出金: -outflows[i],
        調整: -adjVals[i],
        残高: balance,
        net,
        shortfall: balance < 0,
      };
    }).map((row, i, arr) => i === arr.length - 1 ? { ...row, 期末未収入金: endingReceivable, 期末未払費用: endingPayable } : row);
  }, [revenue, expenses, expenseItems, expOverride, months, cfSettings, autoVals, loanSchedule]);

  const shortfallMonths = cfData.filter(d => d.shortfall);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      {shortfallMonths.length > 0 && (
        <div style={{background:"#FFF0F0",border:"1px solid #C0392B",borderLeft:"4px solid #C0392B",borderRadius:"8px",padding:"12px 16px"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:"#C0392B",fontFamily:"'Noto Sans JP',sans-serif",marginBottom:"4px"}}>
            ⚠️ 資金ショートの可能性
          </div>
          <div style={{fontSize:"12px",color:"#C0392B",fontFamily:"'Noto Sans JP',sans-serif"}}>
            {shortfallMonths.map(d=>d.name).join("・")} に残高がマイナスになる見込みです。
          </div>
        </div>
      )}

      <Card>
        <ST>月次キャッシュフロー</ST>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={cfData} barGap={2} margin={{top:8, right:16, bottom:0, left:16}}>
            <XAxis dataKey="name" tick={{fontSize:11, fill:"#888"}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:11, fill:"#888"}} axisLine={false} tickLine={false} tickFormatter={v => `${(v/10000).toFixed(0)}万`}/>
            <Tooltip formatter={(v, n) => [Math.abs(v).toLocaleString() + "円", n]}/>
            <Legend wrapperStyle={{fontSize:'11px'}}/>
            <ReferenceLine y={0} stroke="#ccc"/>
            <Bar dataKey="入金" fill="#2D7A2D" radius={[3,3,0,0]}/>
            <Bar dataKey="出金" fill="#C0392B" radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <ST>月次残高推移（グラフ）</ST>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={cfData} barGap={2} margin={{top:8, right:16, bottom:0, left:16}}>
            <XAxis dataKey="name" tick={{fontSize:11, fill:"#888"}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:11, fill:"#888"}} axisLine={false} tickLine={false} tickFormatter={v => `${(v/10000).toFixed(0)}万`}/>
            <Tooltip formatter={(v, n) => [v.toLocaleString() + "円", n]}/>
            <ReferenceLine y={0} stroke="#ccc"/>
            <Bar dataKey="残高" radius={[3,3,0,0]}>
              {cfData.map((entry, index) => (
                <Cell key={index} fill={entry.残高 < 0 ? "#C0392B" : "#E67E22"}/>
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <ST>月次残高推移</ST>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
              <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"80px"}}>項目</th>
              {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 4px",color:"#888",minWidth:"72px"}}>{m}</th>)}
            </tr></thead>
            <tbody>
              {[
                {label:"入金", color:"#2D7A2D", key:"入金"},
                {label:"出金", color:"#C0392B", key:"出金"},
                {label:"残高", color:ACCENT, key:"残高", bold:true},
              ].map(row=>(
                <tr key={row.label} style={{borderBottom:"1px solid #F0F4F0"}}>
                  <td style={{padding:"5px 8px",fontWeight:row.bold?"700":"400",color:row.color,fontFamily:"'Noto Sans JP',sans-serif"}}>{row.label}</td>
                  {cfData.map((d,i)=>(
                    <td key={i} style={{
                      padding:"5px 4px",textAlign:"right",fontFamily:"monospace",
                      fontWeight:row.bold?"800":"600",
                      color: row.key==="残高" ? (d.残高<0?"#C0392B":ACCENT) : row.color,
                      background: row.key==="残高" && d.残高<0 ? "#FFF0F0" : "transparent",
                    }}>
                      {row.key==="出金"
                        ? Math.abs(d[row.key]).toLocaleString()
                        : d[row.key].toLocaleString()
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <ST>期末繰越メモ</ST>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:"12px"}}>
          <div style={{background:"#F5FAF5",border:"1px solid #D8EAD8",borderRadius:"8px",padding:"12px 14px"}}>
            <div style={{fontSize:"11px",color:"#2D7A2D",fontWeight:"700",marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>期末未収入金</div>
            <div style={{fontSize:"20px",fontWeight:"800",color:"#1A3A1A",fontFamily:"monospace"}}>{(cfData[cfData.length - 1]?.期末未収入金 || 0).toLocaleString()}</div>
            <div style={{fontSize:"11px",color:"#888",marginTop:"4px",lineHeight:1.6,fontFamily:"'Noto Sans JP',sans-serif"}}>翌月・翌々月入金サイトにより次期へ繰り越される売上入金分</div>
          </div>
          <div style={{background:"#FFF5F5",border:"1px solid #F0D6D6",borderRadius:"8px",padding:"12px 14px"}}>
            <div style={{fontSize:"11px",color:"#C0392B",fontWeight:"700",marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>期末未払費用</div>
            <div style={{fontSize:"20px",fontWeight:"800",color:"#5A1A1A",fontFamily:"monospace"}}>{(cfData[cfData.length - 1]?.期末未払費用 || 0).toLocaleString()}</div>
            <div style={{fontSize:"11px",color:"#888",marginTop:"4px",lineHeight:1.6,fontFamily:"'Noto Sans JP',sans-serif"}}>翌月・翌々月出金サイトにより次期へ繰り越される費用支払分</div>
          </div>
        </div>
      </Card>

      <Card>
        <ST>入出金タイミング設定</ST>

        <div style={{background:'#F0F5FF', border:'1px solid #D0DCEF', borderRadius:'8px', padding:'16px', marginBottom:'20px'}}>
          <div style={{marginBottom:"20px"}}>
            <div style={{fontSize:"12px",fontWeight:"700",color:ACCENT,marginBottom:"8px",fontFamily:"'Noto Sans JP',sans-serif"}}>期首現預金残高</div>
            <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
              <NI value={cfSettings.openingBalance || ""} onChange={val=>setCfSettings(prev=>({...prev,openingBalance:val}))} w="120px"/>
              <span style={{fontSize:"11px",color:"#888",fontFamily:"'Noto Sans JP',sans-serif"}}>円</span>
            </div>
          </div>

          <div>
            <div style={{fontSize:"12px",fontWeight:"700",color:ACCENT,marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>期首繰越</div>
            <div style={{fontSize:"11px",color:"#888",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>
              前期からの売掛（繰越入金）・買掛（繰越出金）を入力します。前月分は期首月に、前々月分は期首翌月に加算されます。
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              <div style={{background:"#F5FAF5",border:"1px solid #C8E6C8",borderRadius:"6px",padding:"12px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:"#2D7A2D",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>繰越入金（売掛）</div>
                {[
                  {key:"carryInflow1", timing:"期首月に入金予定"},
                  {key:"carryInflow2", timing:"期首翌月に入金予定"},
                ].map(row=>(
                  <div key={row.key} style={{marginBottom:"8px"}}>
                    <div style={{fontSize:"10px",color:"#2D7A2D",marginBottom:"3px",fontFamily:"'Noto Sans JP',sans-serif"}}>{row.timing}</div>
                    <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                      <NI value={cfSettings[row.key] || ""} onChange={val=>setCfSettings(prev=>({...prev,[row.key]:val}))} w="120px"/>
                      <span style={{fontSize:"11px",color:"#888"}}>円</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{background:"#FFF5F5",border:"1px solid #F5C0C0",borderRadius:"6px",padding:"12px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:"#C0392B",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>繰越出金（買掛）</div>
                {[
                  {key:"carryOutflow1", timing:"期首月に出金予定"},
                  {key:"carryOutflow2", timing:"期首翌月に出金予定"},
                ].map(row=>(
                  <div key={row.key} style={{marginBottom:"8px"}}>
                    <div style={{fontSize:"10px",color:"#C0392B",marginBottom:"3px",fontFamily:"'Noto Sans JP',sans-serif"}}>{row.timing}</div>
                    <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                      <NI value={cfSettings[row.key] || ""} onChange={val=>setCfSettings(prev=>({...prev,[row.key]:val}))} w="120px"/>
                      <span style={{fontSize:"11px",color:"#888"}}>円</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>
          売上ごとの入金サイトと、経費・税金の支払いタイミングを設定します。
          <button onClick={()=>setShowHelp(v=>!v)} style={{marginLeft:"8px",fontSize:"10px",color:ACCENT,background:"none",border:`1px solid ${ACCENT}`,borderRadius:"4px",padding:"1px 6px",cursor:"pointer"}}>
            {showHelp?"▲ 閉じる":"? ヘルプ"}
          </button>
        </div>
        {showHelp && (
          <div style={{background:"#E0F5F5",border:"1px solid #A0D8D8",borderRadius:"6px",padding:"10px 14px",marginBottom:"16px",fontSize:"11px",color:"#1A5A5A",lineHeight:"1.8",fontFamily:"'Noto Sans JP',sans-serif"}}>
            <strong>入金サイト</strong>とは、売上が立った翌月・翌々月に実際に入金される契約上の条件です。<br/>
            <strong>経費出金サイト</strong>は科目ごとに設定可能です。社保・役員報酬など即時出金の科目は「即時（当月）」に設定してください。月末締め翌月払いが一般的なためデフォルトは「翌月出金」です。<br/>
            <strong>税金欄</strong>には消費税（年2回）・法人税（決算後2ヶ月以内）・源泉所得税などを入力してください。<br/>
            <strong>翌月・翌々月サイトの期末月分</strong>は今期の残高には入らず、表下の「期末未収入金」「期末未払費用」に集計されます。
          </div>
        )}

        <div style={{marginBottom:"20px"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:"#2D7A2D",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>売上の入金サイト</div>
          {revenue.map(r=>(
            <div key={r.id} style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"8px",padding:"8px 12px",background:"#F5FAF5",borderRadius:"6px"}}>
              <span style={{fontSize:"12px",color:"#2D7A2D",fontWeight:"600",fontFamily:"'Noto Sans JP',sans-serif",minWidth:"100px"}}>{r.name}</span>
              <div style={{display:"flex",gap:"6px"}}>
                {REV_SITE_OPTIONS.map(opt=>(
                  <button key={opt.value} onClick={()=>setRevSite(r.id, opt.value)} style={{
                    padding:"4px 10px",fontSize:"11px",
                    background:getRevSite(r.id)===opt.value?ACCENT:"#fff",
                    color:getRevSite(r.id)===opt.value?"#fff":"#666",
                    border:`1px solid ${getRevSite(r.id)===opt.value?ACCENT:"#D0D8D0"}`,
                    borderRadius:"4px",cursor:"pointer",fontFamily:"'Noto Sans JP',sans-serif",
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{marginBottom:"20px"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:"#C0392B",marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>経費の出金サイト（科目別）</div>
          <div style={{fontSize:"11px",color:"#888",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>
            デフォルト（一括）を設定し、科目ごとに個別上書きできます。
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"10px",padding:"8px 12px",background:"#FFF5F5",borderRadius:"6px"}}>
            <span style={{fontSize:"12px",color:"#C0392B",fontWeight:"600",fontFamily:"'Noto Sans JP',sans-serif",minWidth:"100px"}}>デフォルト</span>
            <div style={{display:"flex",gap:"6px"}}>
              {EXP_SITE_OPTIONS.map(opt=>(
                <button key={opt.value} onClick={()=>setCfSettings(prev=>({...prev, expSite:opt.value}))} style={{
                  padding:"4px 10px",fontSize:"11px",
                  background:cfSettings.expSite===opt.value?"#C0392B":"#fff",
                  color:cfSettings.expSite===opt.value?"#fff":"#666",
                  border:`1px solid ${cfSettings.expSite===opt.value?"#C0392B":"#D0D8D0"}`,
                  borderRadius:"4px",cursor:"pointer",fontFamily:"'Noto Sans JP',sans-serif",
                }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {expenseItems.filter(item => item.id !== 'insurance').map(item => {
            const currentSite = cfSettings.expSites?.[item.id];
            const isOverridden = currentSite != null;
            return (
              <div key={item.id} style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"6px",padding:"6px 12px",background:isOverridden?"#FFF0F0":"#FAFCFA",borderRadius:"6px",border:isOverridden?"1px solid #F5C0C0":"1px solid transparent"}}>
                <span style={{fontSize:"12px",color:isOverridden?"#C0392B":"#666",fontWeight:isOverridden?"700":"400",fontFamily:"'Noto Sans JP',sans-serif",minWidth:"100px"}}>{item.label}</span>
                <div style={{display:"flex",gap:"4px"}}>
                  {EXP_SITE_OPTIONS.map(opt=>(
                    <button key={opt.value} onClick={()=>setExpItemSite(item.id, opt.value)} style={{
                      padding:"3px 8px",fontSize:"10px",
                      background:getExpSite(item.id)===opt.value?(isOverridden?"#C0392B":"#888"):"#fff",
                      color:getExpSite(item.id)===opt.value?"#fff":"#999",
                      border:`1px solid ${getExpSite(item.id)===opt.value?(isOverridden?"#C0392B":"#aaa"):"#E0E0E0"}`,
                      borderRadius:"4px",cursor:"pointer",fontFamily:"'Noto Sans JP',sans-serif",
                    }}>
                      {opt.label}
                    </button>
                  ))}
                  {isOverridden && (
                    <button onClick={()=>setCfSettings(prev=>{const s={...(prev.expSites||{})};delete s[item.id];return{...prev,expSites:s};})} style={{padding:"3px 6px",fontSize:"10px",color:"#aaa",background:"none",border:"1px solid #E0E0E0",borderRadius:"4px",cursor:"pointer"}}>
                      リセット
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{marginBottom:"12px"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:"#AA7722",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>税金支払い（月別）</div>
          {["消費税","法人税","源泉所得税"].map(taxKey=>(
            <div key={taxKey} style={{marginBottom:"12px"}}>
              <div style={{fontSize:"11px",color:"#AA7722",fontWeight:"700",marginBottom:"6px",fontFamily:"'Noto Sans JP',sans-serif"}}>{taxKey}</div>
              <div style={{display:"flex",gap:"4px",flexWrap:"wrap"}}>
                {months.map(m=>(
                  <div key={m} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"2px"}}>
                    <span style={{fontSize:"10px",color:"#888"}}>{m}</span>
                    <NI value={toN(taxMonths?.[taxKey]?.[m]) || ""} onChange={val=>updateTax(taxKey, m, val)} w="72px" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{marginTop:'16px'}}>
          <div style={{fontSize:'12px', fontWeight:'700', color:'#1A7A7A', marginBottom:'10px', fontFamily:"'Noto Sans JP',sans-serif"}}>
            社会保険料（月別）
          </div>
          <div style={{fontSize:'11px', color:'#888', marginBottom:'8px', fontFamily:"'Noto Sans JP',sans-serif"}}>
            経費設定の自動計算値をデフォルトとして使用。月別に上書き可能。
          </div>
          <div style={{marginBottom:'12px'}}>
            <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
              {months.map(m => {
                const isOverridden = cfSettings.insuranceMonths?.[m] !== undefined && cfSettings.insuranceMonths?.[m] !== '';
                return (
                  <div key={m} style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'3px'}}>
                    <div style={{fontSize:'10px', color:'#888', fontFamily:"'Noto Sans JP',sans-serif"}}>{m}</div>
                    <NI
                      value={isOverridden ? toN(cfSettings.insuranceMonths[m]) : (autoVals.insurance?.[m] ?? 0)}
                      onChange={val => updateInsurance(m, val)}
                      w="72px"
                      style={{
                        background: isOverridden ? '#E8F8FF' : '#F0FAF0',
                      }}
                    />
                    {isOverridden && (
                      <button onClick={() => resetInsuranceMonth(m)} style={{fontSize:'9px', color:'#888', background:'none', border:'none', cursor:'pointer', padding:'0', fontFamily:"'Noto Sans JP',sans-serif"}} title="自動計算値に戻す">
                        ↺ 戻す
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{marginTop:'16px'}}>
          <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px'}}>
            <div style={{fontSize:'12px', fontWeight:'700', color:'#553399', fontFamily:"'Noto Sans JP',sans-serif"}}>
              調整欄
            </div>
            <div style={{fontSize:'11px', color:'#888', fontFamily:"'Noto Sans JP',sans-serif"}}>
              臨時の増減をプラス・マイナスで入力。CF計算に反映されます。
            </div>
          </div>

          {adjustments.map(adj => (
            <div key={adj.id} style={{marginBottom:'12px', padding:'10px 12px', background:'#F8F5FF', border:'1px solid #E0D8F0', borderRadius:'8px'}}>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
                <input value={adj.label} onChange={e => updateAdjustmentLabel(adj.id, e.target.value)} style={{fontSize:'12px', fontWeight:'700', color:'#553399', border:'1px solid #D0C8E8', borderRadius:'4px', padding:'3px 8px', fontFamily:"'Noto Sans JP',sans-serif", background:'#fff', minWidth:'160px'}}/>
                <button onClick={() => removeAdjustment(adj.id)} style={{fontSize:'11px', color:'#ccc', background:'none', border:'none', cursor:'pointer', padding:'2px 4px'}} onMouseEnter={e => e.target.style.color='#C0392B'} onMouseLeave={e => e.target.style.color='#ccc'} title="この調整項目を削除">
                  ✕
                </button>
              </div>

              <div style={{display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px'}}>
                {months.map(m => {
                  const val = adj.monthly?.[m];
                  const num = toN(val);
                  const isPlus = num > 0;
                  const isMinus = String(val || '').startsWith('-') && num < 0;
                  return (
                    <div key={m} style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'3px'}}>
                      <div style={{fontSize:'10px', color:'#888', fontFamily:"'Noto Sans JP',sans-serif"}}>{m}</div>
                      <SignedInput
                        value={val === '' ? '' : val}
                        onChange={v => updateAdjustmentVal(adj.id, m, v)}
                        w="72px"
                        style={{
                          background: isPlus ? '#FFF0F0' : isMinus ? '#F0FAF0' : '#fff',
                          color: isPlus ? '#C0392B' : isMinus ? '#2D7A2D' : '#333',
                          border: `1px solid ${isPlus ? '#F5C0C0' : isMinus ? '#C8E6C8' : '#D0C8E8'}`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <input type="text" value={adj.memo || ''} onChange={e => updateAdjustmentMemo(adj.id, e.target.value)} placeholder="メモ（例：○○さん退職、設備購入など）" style={{width:'100%', boxSizing:'border-box', fontSize:'11px', color:'#666', border:'1px solid #E0D8F0', borderRadius:'4px', padding:'4px 8px', fontFamily:"'Noto Sans JP',sans-serif", background:'#fff'}} />
            </div>
          ))}

          <button onClick={addAdjustment} style={{fontSize:'11px', color:'#553399', background:'none', border:'1px dashed #C8B8E8', borderRadius:'4px', cursor:'pointer', padding:'4px 14px', fontFamily:"'Noto Sans JP',sans-serif"}}>
            ＋ 調整項目を追加
          </button>
        </div>
      </Card>
    </div>
  );
}
